import React, { useEffect, useMemo, useState } from "react";
import { createClientRegistrationToken, fetchClientRegistrationTokens, fetchClientSyncEvents, fetchClientSyncSummary, revokeClientRegistrationToken, runClientConnectionSmokeTest, runClientSyncSelfTest } from "../api/adminApi";

function formatValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}


function RegistrationTokenBox({ apiBase, token, tenantId, onError }) {
  const [label, setLabel] = useState("PC client registration");
  const [expiresDays, setExpiresDays] = useState(30);
  const [tokens, setTokens] = useState([]);
  const [issuedToken, setIssuedToken] = useState("");
  const [busy, setBusy] = useState(false);

  const loadTokens = async () => {
    setBusy(true);
    try {
      const data = await fetchClientRegistrationTokens(apiBase, token, tenantId.trim());
      setTokens(data.items || []);
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const createToken = async () => {
    const targetTenant = tenantId.trim() || "tobe-demo";
    setBusy(true);
    setIssuedToken("");
    try {
      const data = await createClientRegistrationToken(apiBase, token, {
        tenant_id: targetTenant,
        label,
        expires_days: Number(expiresDays) || 30,
      });
      setIssuedToken(data.registration_token || "");
      await loadTokens();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const revokeToken = async (tokenId) => {
    setBusy(true);
    try {
      await revokeClientRegistrationToken(apiBase, token, tokenId);
      await loadTokens();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    loadTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="dashboard-panel" style={{ marginTop: 16 }}>
      <div className="panel-heading">
        <div>
          <p className="panel-kicker">REGISTRATION</p>
          <h3>클라이언트 등록 토큰</h3>
          <p>PC/모바일 클라이언트 초기 연결에 사용할 등록 토큰을 발급합니다. 원문 토큰은 발급 직후 한 번만 표시됩니다.</p>
        </div>
        <button type="button" className="admin-approve-button" onClick={loadTokens} disabled={busy}>
          토큰 새로고침
        </button>
      </div>

      <div className="admin-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 120px auto", gap: 12, marginBottom: 16 }}>
        <label>
          토큰 이름
          <input value={label} onChange={(event) => setLabel(event.target.value)} />
        </label>
        <label>
          유효일
          <input type="number" min="1" max="365" value={expiresDays} onChange={(event) => setExpiresDays(event.target.value)} />
        </label>
        <button type="button" className="admin-approve-button" onClick={createToken} disabled={busy} style={{ alignSelf: "end" }}>
          등록 토큰 발급
        </button>
      </div>

      {issuedToken && (
        <div className="admin-success" style={{ wordBreak: "break-all" }}>
          발급 토큰: {issuedToken}
        </div>
      )}

      {tokens.length === 0 ? (
        <p>발급된 등록 토큰이 없습니다.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>회사</th>
                <th>이름</th>
                <th>상태</th>
                <th>만료</th>
                <th>최근 사용</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.tenant_id}</td>
                  <td>{item.label}</td>
                  <td>{item.status}</td>
                  <td>{formatValue(item.expires_at)}</td>
                  <td>{formatValue(item.last_used_at)}</td>
                  <td>
                    {item.status === "active" && (
                      <button type="button" className="admin-danger-button" onClick={() => revokeToken(item.id)} disabled={busy}>
                        폐기
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function ClientConnectionSmokeBox({ apiBase, onDone, onError }) {
  const [registrationToken, setRegistrationToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const runSmoke = async () => {
    const token = registrationToken.trim();
    if (!token) {
      onError("등록 토큰을 입력해주세요.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const data = await runClientConnectionSmokeTest(apiBase, token);
      setResult(data);
      await onDone();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dashboard-panel" style={{ marginTop: 16 }}>
      <div className="panel-heading">
        <div>
          <p className="panel-kicker">CONNECTION TEST</p>
          <h3>클라이언트 연결 테스트</h3>
          <p>발급한 등록 토큰으로 장치 등록, 이벤트 업로드, 학습 번들 업로드, 서버 pull을 실제 API 순서대로 검증합니다.</p>
        </div>
      </div>
      <div className="admin-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12 }}>
        <label>
          등록 토큰
          <input
            value={registrationToken}
            onChange={(event) => setRegistrationToken(event.target.value)}
            placeholder="tobe_reg_..."
            autoComplete="off"
          />
        </label>
        <button type="button" className="admin-approve-button" onClick={runSmoke} disabled={busy} style={{ alignSelf: "end" }}>
          {busy ? "테스트 중" : "연결 테스트 실행"}
        </button>
      </div>
      {result && (
        <div className="admin-success" style={{ marginTop: 12 }}>
          연결 테스트 완료: {result.tenant_id} / {result.device_id} / 이벤트 {result.event_received}건 / 학습 번들 #{result.bundle_id}
        </div>
      )}
    </div>
  );
}

function ClientSyncPanel({ apiBase, token }) {
  const [tenantId, setTenantId] = useState("");
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const selectedTenantId = tenantId.trim();

  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [summaryData, eventData] = await Promise.all([
        fetchClientSyncSummary(apiBase, token, selectedTenantId),
        fetchClientSyncEvents(apiBase, token, selectedTenantId),
      ]);
      setSummary(summaryData);
      setEvents(eventData.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelfTest = async () => {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const result = await runClientSyncSelfTest(apiBase, token, selectedTenantId || "tobe-demo");
      setMessage(`자체 테스트 완료: ${result.tenant_id} / ${result.device_id}`);
      await loadAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = summary?.totals || {};
  const devices = summary?.latest_devices || [];
  const bundles = summary?.latest_learning_bundles || [];
  const policy = summary?.sync_policy || {};
  const latestDevice = devices[0];

  const statusText = useMemo(() => {
    if (!summary) return "확인 전";
    if ((totals.devices || 0) === 0) return "등록 대기";
    if ((totals.learning_bundles || 0) === 0) return "학습 업로드 대기";
    return "정상 수신";
  }, [summary, totals.devices, totals.learning_bundles]);

  return (
    <section className="dashboard-panel">
      <div className="panel-heading">
        <div>
          <p className="panel-kicker">CLIENT SYNC</p>
          <h3>PC/모바일 독립 AI 동기화</h3>
          <p>
            각 회사의 PC 클라이언트와 모바일 AI가 오프라인에서 일한 기록을 서버로 업로드하고,
            서버의 회사 AI 지식으로 다시 내려받는 흐름을 점검합니다.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="admin-approve-button" onClick={handleSelfTest} disabled={loading}>
            테스트 데이터 생성
          </button>
          <button type="button" className="admin-approve-button" onClick={loadAll} disabled={loading}>
            {loading ? "확인 중" : "새로고침"}
          </button>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {message && <div className="admin-success">{message}</div>}

      <div className="admin-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, marginBottom: 16 }}>
        <label>
          회사 ID 필터
          <input value={tenantId} onChange={(event) => setTenantId(event.target.value)} placeholder="비우면 전체, 예: tobe-demo" />
        </label>
        <button type="button" className="admin-approve-button" onClick={loadAll} disabled={loading} style={{ alignSelf: "end" }}>
          조회
        </button>
      </div>

      <div className="admin-stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 12 }}>
        <article className="admin-stat-card">
          <span>동기화 상태</span>
          <strong>{statusText}</strong>
        </article>
        <article className="admin-stat-card">
          <span>등록 장치</span>
          <strong>{totals.devices || 0}</strong>
        </article>
        <article className="admin-stat-card">
          <span>수신 이벤트</span>
          <strong>{totals.events || 0}</strong>
        </article>
        <article className="admin-stat-card">
          <span>학습 번들</span>
          <strong>{totals.learning_bundles || 0}</strong>
        </article>
        <article className="admin-stat-card">
          <span>오프라인 우선</span>
          <strong>{policy.offline_first ? "ON" : "OFF"}</strong>
        </article>
      </div>

      <RegistrationTokenBox apiBase={apiBase} token={token} tenantId={tenantId} onError={setError} />

      <ClientConnectionSmokeBox apiBase={apiBase} onDone={loadAll} onError={setError} />

      <div className="dashboard-panel" style={{ marginTop: 16 }}>
        <div className="panel-heading">
          <div>
            <p className="panel-kicker">LAST DEVICE</p>
            <h3>최근 접속 장치</h3>
          </div>
        </div>
        {latestDevice ? (
          <div className="admin-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
            <article className="admin-stat-card">
              <span>장치명</span>
              <strong>{latestDevice.client_name || latestDevice.device_id}</strong>
            </article>
            <article className="admin-stat-card">
              <span>플랫폼</span>
              <strong>{formatValue(latestDevice.platform)}</strong>
            </article>
            <article className="admin-stat-card">
              <span>로컬 모델</span>
              <strong>{formatValue(latestDevice.local_model)}</strong>
            </article>
          </div>
        ) : (
          <p>아직 등록된 클라이언트가 없습니다. 테스트 데이터 생성으로 파이프라인을 먼저 확인할 수 있습니다.</p>
        )}
      </div>

      <div className="dashboard-panel" style={{ marginTop: 16 }}>
        <div className="panel-heading">
          <div>
            <p className="panel-kicker">DEVICES</p>
            <h3>등록 장치</h3>
          </div>
        </div>
        {devices.length === 0 ? (
          <p>등록된 장치가 없습니다.</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>회사</th>
                  <th>장치</th>
                  <th>유형</th>
                  <th>플랫폼</th>
                  <th>로컬 모델</th>
                  <th>최근 접속</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((device) => (
                  <tr key={`${device.tenant_id}-${device.device_id}`}>
                    <td>{device.tenant_id}</td>
                    <td>{device.client_name || device.device_id}</td>
                    <td>{formatValue(device.device_type)}</td>
                    <td>{formatValue(device.platform)}</td>
                    <td>{formatValue(device.local_model)}</td>
                    <td>{formatValue(device.last_seen_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="dashboard-panel" style={{ marginTop: 16 }}>
        <div className="panel-heading">
          <div>
            <p className="panel-kicker">EVENTS</p>
            <h3>최근 동기화 이벤트</h3>
          </div>
        </div>
        {events.length === 0 ? (
          <p>수신된 이벤트가 없습니다.</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>회사</th>
                  <th>장치</th>
                  <th>유형</th>
                  <th>시간</th>
                  <th>수신일</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id}>
                    <td>{event.id}</td>
                    <td>{event.tenant_id}</td>
                    <td>{event.device_id}</td>
                    <td>{formatValue(event.event_type)}</td>
                    <td>{formatValue(event.event_time)}</td>
                    <td>{formatValue(event.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="dashboard-panel" style={{ marginTop: 16 }}>
        <div className="panel-heading">
          <div>
            <p className="panel-kicker">LEARNING</p>
            <h3>최근 학습 번들</h3>
          </div>
        </div>
        {bundles.length === 0 ? (
          <p>업로드된 학습 번들이 없습니다.</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>회사</th>
                  <th>장치</th>
                  <th>유형</th>
                  <th>요약</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                {bundles.map((bundle) => (
                  <tr key={bundle.id}>
                    <td>{bundle.id}</td>
                    <td>{bundle.tenant_id}</td>
                    <td>{bundle.device_id}</td>
                    <td>{bundle.bundle_type}</td>
                    <td>{bundle.summary || "-"}</td>
                    <td>{bundle.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

export default ClientSyncPanel;
